import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { backendApi } from '../lib/backendApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { formatBusinessDateTime } from '../lib/dateTime';

const SalesHistoryPage = () => {
  const { api, user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const formatCurrency = (amount) => `UGX ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const params = useMemo(() => {
    const p = {};
    if (startDate) p.start_date = new Date(startDate).toISOString();
    if (endDate) p.end_date = new Date(`${endDate}T23:59:59`).toISOString();
    if (paymentMethod !== 'all') p.payment_method = paymentMethod;
    return p;
  }, [startDate, endDate, paymentMethod]);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await backendApi.sales.list(api, params);
      setSales(res.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load sales history');
    } finally {
      setLoading(false);
    }
  }, [api, params]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const openSale = async (saleId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await backendApi.sales.getOne(api, saleId);
      setSelectedSale(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load sale details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-history-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Sales History</h1>
        <p className="text-muted-foreground">
          {user?.role === 'cashier'
            ? 'Viewing your own transactions.'
            : 'Review transactions and open full receipt details.'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter by date range and payment method.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger><SelectValue placeholder="Payment method" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="mtn_mobile_money">MTN MoMo</SelectItem>
              <SelectItem value="airtel_money">Airtel Money</SelectItem>
              <SelectItem value="bank_card">Card</SelectItem>
              <SelectItem value="credit">Credit</SelectItem>
              <SelectItem value="wallet">Wallet</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchSales} disabled={loading}>
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</> : 'Apply'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
          <CardDescription>{sales.length} sale(s) found</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Receipt</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Debt</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sales.map((sale) => (
                <TableRow key={sale.id}>
                  <TableCell>{formatBusinessDateTime(sale.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{sale.receipt_number}</TableCell>
                  <TableCell>{sale.customer_name || '-'}</TableCell>
                  <TableCell><Badge variant="outline">{sale.payment_method}</Badge></TableCell>
                  <TableCell className="font-mono">{formatCurrency(sale.total_amount)}</TableCell>
                  <TableCell className="font-mono text-destructive">{formatCurrency(sale.credit_amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => openSale(sale.id)}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!sales.length && !loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No sales found for the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Sale Details
            </DialogTitle>
            <DialogDescription>
              {selectedSale ? selectedSale.receipt_number : 'Loading...'}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : selectedSale ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <p>Cashier: <strong>{selectedSale.cashier_name}</strong></p>
                <p>Customer: <strong>{selectedSale.customer_name || 'Walk-in'}</strong></p>
                <p>Method: <strong>{selectedSale.payment_method}</strong></p>
                <p>Date: <strong>{formatBusinessDateTime(selectedSale.created_at)}</strong></p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedSale.items || []).map((item, idx) => (
                    <TableRow key={`${item.product_id}-${idx}`}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell>{formatCurrency(item.unit_price * item.quantity - (item.discount_amount || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Total</span><span className="font-mono">{formatCurrency(selectedSale.total_amount)}</span></div>
                <div className="flex justify-between"><span>Paid</span><span className="font-mono">{formatCurrency(selectedSale.amount_paid)}</span></div>
                {Number(selectedSale.wallet_amount || 0) > 0 && (
                  <div className="flex justify-between"><span>Wallet Used</span><span className="font-mono">{formatCurrency(selectedSale.wallet_amount)}</span></div>
                )}
                {Number(selectedSale.credit_amount || 0) > 0 && (
                  <div className="flex justify-between text-destructive"><span>Credit</span><span className="font-mono">{formatCurrency(selectedSale.credit_amount)}</span></div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesHistoryPage;
